import React from 'react'
import useSWRImmutable from 'swr/immutable'

import { getCompany, getCompanySubDivisions } from '../../../api/companies'
import Loading from '../../components/layout/Loading'
import ArrowRight from '../../../assets/icons/arrow-right.svg?react'
import { CompanySubDivision } from '../../../api/companies/types'
import { useI18N } from '../../../libraries/i18n/hook'
import SeeAllLink from '../../components/form/SeeAllLink'
import PartnerIcon from '../../components/companies/PartnerIcon'

type Props = { companyId: number, divisionId: number }
const CompanySubDivisions: React.FC<Props> = ({ companyId, divisionId }) => {
  const { t } = useI18N()
  const { data: companyData, isLoading: isLoadingCompany } = useSWRImmutable(['company', companyId], () => getCompany(companyId))
  const { data, isLoading } = useSWRImmutable(['company', companyId, 'divisions', divisionId], () => getCompanySubDivisions(companyId, divisionId))

  if(isLoading || isLoadingCompany) {
    return <Loading />
  }

  const getLink = (subDivision: CompanySubDivision, highlightedCard: boolean) => {
    const arrowIconClass = `w-4 ml-1 inline-block ${highlightedCard ? 'w-9 fill-white' : 'fill-deep-green'}`
    if(subDivision.positions && subDivision.positions.length > 0) {
      return (
        <SeeAllLink
          label={t('common.seeDetails', undefined, companyData?.languageCode)}
          linkTo={`/companies/${companyId}/divisions/${divisionId}/sub-divisions/${subDivision.id}`}
          iconClassName={arrowIconClass}
          linkColor={highlightedCard ? 'white' : 'black'}
          size='lg'
        />
      )
    } else if (subDivision.externalUrl) {
      return (
        <a href={subDivision.externalUrl} target='_blank' rel='noreferrer'>
          <span className='text-sm sm:text-base font-semibold'>{t('common.seeMore', undefined, companyData?.languageCode)} <ArrowRight className={arrowIconClass} /></span>
        </a>
      )
    }
    return null
  }

  return (
    <div className='flex flex-col gap-y-4 sm:mt-6 sm:grid sm:grid-cols-3 sm:gap-4'>
      {data && data.length === 0 && <div>No subdivisions divisions found</div>}
      {data?.map((subDivision) => {
        const highlightedCard = !!(subDivision.positions && subDivision.positions.length > 0)
        return (
          <div key={subDivision.name} className={`rounded-lg relative overflow-hidden ${highlightedCard ? 'bg-deep-green' : 'bg-soft-green'}`}>
            <span className='absolute right-5 top-5'>{companyData?.isPartner && highlightedCard && <PartnerIcon color='white' />}</span>
            <div className={`z-20 relative my-2 sm:my-4 sm:my-0 p-6 rounded-lg ${highlightedCard ? 'text-white' : 'text-black'}`}>
              <h4>{subDivision.name}</h4>
              <p className='my-2 line-clamp-3'>{subDivision.description}</p>
              <div className='text-end'>{getLink(subDivision, highlightedCard)}</div>
            </div>
            {highlightedCard && (
              <>
                <div className='z-10 absolute -bottom-48 -right-48 bg-soft-green opacity-5 h-96 w-96 rounded-full' />
                <div className='z-10 absolute -bottom-32 -right-32 bg-soft-green opacity-5 h-64 w-64 rounded-full' />
                <div className='z-10 absolute -bottom-20 -right-20 bg-soft-green opacity-5 h-40 w-40 rounded-full' />
              </>
            )}
          </div>
        )
      })}
    </div>
  )
}

export default CompanySubDivisions
